import { Router } from "express";
import { z } from "zod";
import { and, asc, count, desc, eq, inArray, ne, sql, type SQL } from "drizzle-orm";
import { db } from "../../db/index";
import { attendances, employeeShifts, employeeShiftSchedules, wfhRequests, attendanceCorrections } from "../../db/schema/attendance";
import { users } from "../../db/schema/identity";
import { employeeDetails } from "../../db/schema/employee";
import { teams } from "../../db/schema/hr";
import { leaves } from "../../db/schema/leave";
import { holidays } from "../../db/schema/hr";
import { companies } from "../../db/schema/tenancy";
import { requireAuth, requirePermission, getEffectivePermissions, SCOPE } from "../../rbac/index";

/** Does this user see EVERYONE's attendance (admin/HR), or just their own? (view_attendance=ALL) */
async function seesAll(userId: number): Promise<boolean> {
  const eff = await getEffectivePermissions(userId);
  return eff["view_attendance"] === SCOPE.ALL;
}
const CORRECTION_CAP = 3; // employee self-filed corrections per calendar month (admins exempt)

/** Gate: requires view_attendance = ALL (admin/HR). Used by company-wide views + shift/roster writes. */
const onlyAllAttendance = [
  requirePermission("view_attendance"),
  (req: import("express").Request, res: import("express").Response, next: import("express").NextFunction) =>
    req.scope === SCOPE.ALL ? next() : res.status(403).json({ error: "Forbidden", permission: "view_attendance" }),
];

/**
 * B3 Attendance & Shifts (reskin-docs/05; legacy AttendanceController + V2 AttendanceController +
 * MyAttendanceController + ShiftController). Clock-in/out with shift-based late/half-day (doc §3),
 * daily session pairing (buildDaySessions), Matrix/Summary for admin/HR, shift list + roster.
 * Company-scoped; admin views gate view_attendance. Times stored UTC, compared in company tz.
 * Penalty engine + Regularization = legacy 501 stubs → not built. WFH/Corrections = BiometricSync
 * tables not yet ported → flagged follow-up.
 */
export const attendanceRouter = Router();
attendanceRouter.use(requireAuth);

const minutesOfDay = (t?: string | null): number => {
  if (!t) return 0;
  const [h, m] = t.split(":");
  return Number(h) * 60 + Number(m);
};
async function companyTz(companyId: number): Promise<string> {
  const [c] = await db.select({ tz: companies.timezone }).from(companies).where(eq(companies.id, companyId)).limit(1);
  return c?.tz || "Asia/Kolkata";
}
/** "YYYY-MM-DD" / minutes-of-day for `now` in a timezone (Intl — no Date tz juggling). */
function nowInTz(tz: string): { date: string; minutes: number } {
  const f = new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false });
  const parts: Record<string, string> = {};
  for (const p of f.formatToParts(new Date())) parts[p.type] = p.value;
  return { date: `${parts.year}-${parts.month}-${parts.day}`, minutes: Number(parts.hour) * 60 + Number(parts.minute) };
}
const isoDateOf = (v: unknown) => (v instanceof Date ? v.toISOString().slice(0, 10) : String(v).slice(0, 10));

type Shift = { id: number | null; officeStartTime: string; officeEndTime: string; lateMarkDuration: number; halfdayMarkTime: string | null; shiftType: string };

/** The employee's shift for a date: roster (employee_shift_schedules) → else company default → else first shift. */
async function shiftForUser(userId: number, companyId: number, date: string): Promise<Shift> {
  const sched = await db
    .select({ id: employeeShifts.id, s: employeeShifts.officeStartTime, e: employeeShifts.officeEndTime, lmd: employeeShifts.lateMarkDuration, hmt: employeeShifts.halfdayMarkTime, t: employeeShifts.shiftType })
    .from(employeeShiftSchedules)
    .innerJoin(employeeShifts, eq(employeeShifts.id, employeeShiftSchedules.employeeShiftId))
    .where(and(eq(employeeShiftSchedules.userId, userId), sql`${employeeShiftSchedules.date} = ${date}`))
    .limit(1);
  let row = sched[0];
  if (!row) {
    const def = await db
      .select({ id: employeeShifts.id, s: employeeShifts.officeStartTime, e: employeeShifts.officeEndTime, lmd: employeeShifts.lateMarkDuration, hmt: employeeShifts.halfdayMarkTime, t: employeeShifts.shiftType })
      .from(employeeShifts).where(eq(employeeShifts.companyId, companyId)).orderBy(asc(employeeShifts.id)).limit(1);
    row = def[0];
  }
  if (!row) return { id: null, officeStartTime: "09:00:00", officeEndTime: "18:00:00", lateMarkDuration: 15, halfdayMarkTime: null, shiftType: "strict" };
  return { id: row.id, officeStartTime: row.s, officeEndTime: row.e, lateMarkDuration: row.lmd, halfdayMarkTime: row.hmt, shiftType: row.t };
}

/** Pair a day's attendance rows into sessions + in-office/break minutes (buildDaySessions, simplified). */
function buildSessions(rows: { clockIn: Date; clockOut: Date | null }[]) {
  const sessions = rows.map((r) => {
    const inT = new Date(r.clockIn).getTime();
    const outT = r.clockOut ? new Date(r.clockOut).getTime() : null;
    return { in: new Date(r.clockIn).toISOString(), out: r.clockOut ? new Date(r.clockOut).toISOString() : null, minutes: outT ? Math.round((outT - inT) / 60000) : null, open: !r.clockOut };
  });
  const inOfficeMinutes = sessions.reduce((s, x) => s + (x.minutes ?? 0), 0);
  let breakMinutes = 0;
  for (let i = 1; i < rows.length; i++) {
    const prev = rows[i - 1], curr = rows[i];
    if (prev?.clockOut && curr) breakMinutes += Math.max(0, Math.round((new Date(curr.clockIn).getTime() - new Date(prev.clockOut).getTime()) / 60000));
  }
  return { sessions, inOfficeMinutes, breakMinutes };
}

/** POST /api/attendance/clock-in — self clock-in; computes late/half-day from the shift (doc §3). */
attendanceRouter.post("/attendance/clock-in", async (req, res) => {
  const userId = req.auth!.userId;
  const companyId = req.auth!.companyId;
  const [open] = await db.select({ id: attendances.id }).from(attendances).where(and(eq(attendances.userId, userId), sql`${attendances.clockOutTime} is null`)).limit(1);
  if (open) return res.status(422).json({ error: "You're already clocked in" });

  const tz = await companyTz(companyId);
  const { date, minutes: nowMin } = nowInTz(tz);
  const shift = await shiftForUser(userId, companyId, date);
  const [todayRow] = await db.select({ c: count() }).from(attendances).where(and(eq(attendances.userId, userId), sql`DATE(${attendances.clockInTime}) = ${date}`));
  const isFirst = Number(todayRow?.c ?? 0) === 0;

  const [halfLeave] = await db.select({ id: leaves.id }).from(leaves).where(and(eq(leaves.userId, userId), eq(leaves.status, "approved"), sql`${leaves.leaveDate} = ${date}`, eq(leaves.duration, "half day"))).limit(1);

  const late = nowMin > minutesOfDay(shift.officeStartTime) + (shift.lateMarkDuration || 0);
  const half = (!!shift.halfdayMarkTime && isFirst && nowMin > minutesOfDay(shift.halfdayMarkTime)) || !!halfLeave;
  const crossMidnight = minutesOfDay(shift.officeStartTime) > minutesOfDay(shift.officeEndTime);
  const endDate = crossMidnight ? isoDateOf(new Date(new Date(date).getTime() + 86400000)) : date;
  const shiftStart = new Date(`${date}T${shift.officeStartTime}`);
  const shiftEnd = new Date(`${endDate}T${shift.officeEndTime}`);

  await db.insert(attendances).values({
    companyId, userId, clockInTime: new Date(), clockInIp: (req.ip || "127.0.0.1").slice(0, 250),
    late: late ? "yes" : "no", halfDay: half ? "yes" : "no",
    employeeShiftId: shift.id ?? undefined, shiftStartTime: shiftStart, shiftEndTime: shiftEnd,
  } as never);
  res.status(201).json({ ok: true, late, halfDay: half, message: late ? "Clocked in (late)" : "Clocked in" });
});

/** POST /api/attendance/clock-out — close the open session. */
attendanceRouter.post("/attendance/clock-out", async (req, res) => {
  const userId = req.auth!.userId;
  const [open] = await db.select({ id: attendances.id }).from(attendances).where(and(eq(attendances.userId, userId), sql`${attendances.clockOutTime} is null`)).orderBy(sql`${attendances.clockInTime} desc`).limit(1);
  if (!open) return res.status(422).json({ error: "You're not clocked in" });
  await db.update(attendances).set({ clockOutTime: new Date(), clockOutIp: (req.ip || "127.0.0.1").slice(0, 250) }).where(eq(attendances.id, open.id));
  res.json({ ok: true, message: "Clocked out" });
});

/** GET /api/attendance/today — my today panel: sessions + hours + break + punch state. */
attendanceRouter.get("/attendance/today", async (req, res) => {
  const userId = Number(req.query.user_id) && req.scope === SCOPE.ALL ? Number(req.query.user_id) : req.auth!.userId;
  const tz = await companyTz(req.auth!.companyId);
  const { date } = nowInTz(tz);
  const rows = await db
    .select({ clockIn: attendances.clockInTime, clockOut: attendances.clockOutTime, late: attendances.late })
    .from(attendances)
    .where(and(eq(attendances.userId, userId), sql`DATE(${attendances.clockInTime}) = ${date}`))
    .orderBy(asc(attendances.clockInTime));
  const { sessions, inOfficeMinutes, breakMinutes } = buildSessions(rows as { clockIn: Date; clockOut: Date | null }[]);
  res.json({
    date, sessions, inOfficeMinutes, breakMinutes,
    isOpen: sessions.some((s) => s.open),
    late: rows.some((r) => r.late === "yes"),
  });
});

/** GET /api/attendance/my?month=YYYY-MM — my month day-by-day status. */
attendanceRouter.get("/attendance/my", async (req, res) => {
  const userId = req.auth!.userId;
  const month = (req.query.month as string) || new Date().toISOString().slice(0, 7);
  const start = `${month}-01`;
  const end = `${month}-31`;
  const rows = await db
    .select({ clockIn: attendances.clockInTime, clockOut: attendances.clockOutTime, late: attendances.late, halfDay: attendances.halfDay })
    .from(attendances)
    .where(and(eq(attendances.userId, userId), sql`DATE(${attendances.clockInTime}) BETWEEN ${start} AND ${end}`))
    .orderBy(asc(attendances.clockInTime));
  const byDay: Record<string, { present: boolean; late: boolean; half: boolean; minutes: number }> = {};
  for (const r of rows) {
    const d = isoDateOf(r.clockIn);
    const e = (byDay[d] ??= { present: true, late: false, half: false, minutes: 0 });
    if (r.late === "yes") e.late = true;
    if (r.halfDay === "yes") e.half = true;
    if (r.clockOut) e.minutes += Math.round((new Date(r.clockOut).getTime() - new Date(r.clockIn).getTime()) / 60000);
  }
  res.json({ month, days: Object.entries(byDay).map(([date, v]) => ({ date, ...v })).sort((a, b) => a.date.localeCompare(b.date)) });
});

/* ── Admin/HR views (view_attendance) ── */

/** Staff = users with employee_details, company-scoped. */
async function staffOf(companyId: number, search: string, deptId: number) {
  const conds: SQL[] = [eq(users.companyId, companyId), ne(users.status, "deactive")];
  if (search) conds.push(sql`${users.name} like ${`%${search}%`}`);
  if (deptId) conds.push(eq(employeeDetails.departmentId, deptId));
  return db
    .select({ id: users.id, name: users.name, image: users.image, code: employeeDetails.employeeId, dept: teams.teamName })
    .from(users)
    .innerJoin(employeeDetails, eq(employeeDetails.userId, users.id))
    .leftJoin(teams, eq(teams.id, employeeDetails.departmentId))
    .where(and(...conds))
    .orderBy(asc(users.name));
}

/** Per-day status for a staff set over [from,to]: holiday > leave > attendance(late?L:P) > weekend-off > absent. */
async function statusGrid(companyId: number, staffIds: number[], from: string, to: string) {
  const att = staffIds.length
    ? await db.select({ userId: attendances.userId, clockIn: attendances.clockInTime, clockOut: attendances.clockOutTime, late: attendances.late })
        .from(attendances).where(and(inArray(attendances.userId, staffIds), sql`DATE(${attendances.clockInTime}) BETWEEN ${from} AND ${to}`))
    : [];
  const lv = staffIds.length
    ? await db.select({ userId: leaves.userId, date: leaves.leaveDate }).from(leaves).where(and(inArray(leaves.userId, staffIds), eq(leaves.status, "approved"), sql`${leaves.leaveDate} BETWEEN ${from} AND ${to}`))
    : [];
  const hol = await db.select({ date: holidays.date }).from(holidays).where(and(eq(holidays.companyId, companyId), sql`${holidays.date} BETWEEN ${from} AND ${to}`));
  const holiday = new Set(hol.map((h) => isoDateOf(h.date)));
  const attBy: Record<string, { late: boolean; minutes: number }> = {};
  for (const a of att) {
    const k = `${a.userId}|${isoDateOf(a.clockIn)}`;
    const e = (attBy[k] ??= { late: false, minutes: 0 });
    if (a.late === "yes") e.late = true;
    if (a.clockOut) e.minutes += Math.round((new Date(a.clockOut).getTime() - new Date(a.clockIn).getTime()) / 60000);
  }
  const leaveSet = new Set(lv.map((l) => `${l.userId}|${isoDateOf(l.date)}`));
  return { attBy, leaveSet, holiday };
}

function eachDay(from: string, to: string): string[] {
  const out: string[] = [];
  for (let d = new Date(from); d <= new Date(to); d.setUTCDate(d.getUTCDate() + 1)) out.push(d.toISOString().slice(0, 10));
  return out;
}
function statusFor(userId: number, day: string, g: { attBy: Record<string, { late: boolean; minutes: number }>; leaveSet: Set<string>; holiday: Set<string> }): string {
  if (g.holiday.has(day)) return "holiday";
  if (g.leaveSet.has(`${userId}|${day}`)) return "leave";
  const a = g.attBy[`${userId}|${day}`];
  if (a) return a.late ? "late" : "present";
  const wd = new Date(day).getUTCDay();
  if (wd === 0 || wd === 6) return "off";
  return "absent";
}

/** GET /api/attendance/matrix?from&to&search&dept — staff × day status grid. */
attendanceRouter.get("/attendance/matrix", ...onlyAllAttendance, async (req, res) => {
  const companyId = req.auth!.companyId;
  const now = new Date();
  const from = (req.query.from as string) || `${now.toISOString().slice(0, 7)}-01`;
  const to = (req.query.to as string) || now.toISOString().slice(0, 10);
  const staff = await staffOf(companyId, (req.query.search as string) || "", Number(req.query.dept) || 0);
  const days = eachDay(from, to).slice(0, 40);
  const g = await statusGrid(companyId, staff.map((s) => s.id), from, to);
  res.json({
    from, to, days,
    staff: staff.map((s) => ({ ...s, cells: days.map((d) => ({ date: d, status: statusFor(s.id, d, g) })) })),
  });
});

/** GET /api/attendance/summary?from&to&search&dept — per-employee counts over the window. */
attendanceRouter.get("/attendance/summary", ...onlyAllAttendance, async (req, res) => {
  const companyId = req.auth!.companyId;
  const now = new Date();
  const from = (req.query.from as string) || `${now.toISOString().slice(0, 7)}-01`;
  const to = (req.query.to as string) || now.toISOString().slice(0, 10);
  const staff = await staffOf(companyId, (req.query.search as string) || "", Number(req.query.dept) || 0);
  const days = eachDay(from, to);
  const g = await statusGrid(companyId, staff.map((s) => s.id), from, to);
  res.json({
    from, to,
    rows: staff.map((s) => {
      let present = 0, late = 0, leave = 0, absent = 0, minutes = 0;
      for (const d of days) {
        const st = statusFor(s.id, d, g);
        if (st === "present") present++;
        else if (st === "late") { present++; late++; }
        else if (st === "leave") leave++;
        else if (st === "absent") absent++;
        minutes += g.attBy[`${s.id}|${d}`]?.minutes ?? 0;
      }
      return { id: s.id, name: s.name, code: s.code, dept: s.dept, present, late, leave, absent, hours: Math.round((minutes / 60) * 10) / 10 };
    }),
  });
});

/* ── Shifts (ShiftController) ── */
attendanceRouter.get("/shifts", ...onlyAllAttendance, async (req, res) => {
  const rows = await db
    .select({ id: employeeShifts.id, name: employeeShifts.shiftName, start: employeeShifts.officeStartTime, end: employeeShifts.officeEndTime, grace: employeeShifts.lateMarkDuration, type: employeeShifts.shiftType, halfday: employeeShifts.halfdayMarkTime })
    .from(employeeShifts).where(eq(employeeShifts.companyId, req.auth!.companyId)).orderBy(asc(employeeShifts.shiftName));
  // assigned headcount per shift (latest schedule)
  const counts = await db.select({ sid: employeeShiftSchedules.employeeShiftId, c: count() }).from(employeeShiftSchedules).groupBy(employeeShiftSchedules.employeeShiftId);
  const by: Record<number, number> = {};
  for (const c of counts) if (c.sid != null) by[Number(c.sid)] = Number(c.c);
  res.json({ rows: rows.map((r) => ({ ...r, assigned: by[r.id] ?? 0 })) });
});

const shiftBody = z.object({
  shift_name: z.string().trim().min(1).max(255),
  office_start_time: z.string().min(4),
  office_end_time: z.string().min(4),
  late_mark_duration: z.coerce.number().int().min(0).max(255).default(0),
  halfday_mark_time: z.string().optional().nullable(),
  shift_type: z.enum(["strict", "flexible"]).default("strict"),
});
const hhmmss = (t: string) => (t.length === 5 ? `${t}:00` : t);

/** POST/PUT/DELETE /shifts — shift CRUD (admin/HR). */
attendanceRouter.post("/shifts", ...onlyAllAttendance, async (req, res) => {
  const p = shiftBody.safeParse(req.body);
  if (!p.success) return res.status(422).json({ error: "Invalid payload", details: p.error.flatten() });
  const [r] = await db.insert(employeeShifts).values({
    companyId: req.auth!.companyId, shiftName: p.data.shift_name, shiftShortCode: p.data.shift_name.slice(0, 12), color: "#6366f1",
    officeStartTime: hhmmss(p.data.office_start_time), officeEndTime: hhmmss(p.data.office_end_time),
    lateMarkDuration: p.data.late_mark_duration, halfdayMarkTime: p.data.halfday_mark_time ? hhmmss(p.data.halfday_mark_time) : null, shiftType: p.data.shift_type,
    clockinInDay: 1, officeOpenDays: '["1","2","3","4","5"]',
  } as never).$returningId();
  res.status(201).json({ id: (r as { id: number }).id, message: "Shift created" });
});
attendanceRouter.put("/shifts/:id", ...onlyAllAttendance, async (req, res) => {
  const id = Number(req.params.id);
  const p = shiftBody.safeParse(req.body);
  if (!p.success) return res.status(422).json({ error: "Invalid payload", details: p.error.flatten() });
  const [exists] = await db.select({ id: employeeShifts.id }).from(employeeShifts).where(and(eq(employeeShifts.id, id), eq(employeeShifts.companyId, req.auth!.companyId))).limit(1);
  if (!exists) return res.status(404).json({ error: "Not found" });
  await db.update(employeeShifts).set({ shiftName: p.data.shift_name, officeStartTime: hhmmss(p.data.office_start_time), officeEndTime: hhmmss(p.data.office_end_time), lateMarkDuration: p.data.late_mark_duration, halfdayMarkTime: p.data.halfday_mark_time ? hhmmss(p.data.halfday_mark_time) : null, shiftType: p.data.shift_type } as never).where(eq(employeeShifts.id, id));
  res.json({ id, message: "Shift updated" });
});
attendanceRouter.delete("/shifts/:id", ...onlyAllAttendance, async (req, res) => {
  const id = Number(req.params.id);
  const [used] = await db.select({ c: count() }).from(employeeShiftSchedules).where(eq(employeeShiftSchedules.employeeShiftId, id));
  if (Number(used?.c ?? 0) > 0) return res.status(422).json({ error: "Shift is assigned to employees — reassign them first" });
  await db.delete(employeeShifts).where(and(eq(employeeShifts.id, id), eq(employeeShifts.companyId, req.auth!.companyId)));
  res.json({ id, message: "Shift deleted" });
});

/** POST /shifts/:id/assign {user_id, date} — roster: put an employee on this shift for a date (upsert). */
attendanceRouter.post("/shifts/:id/assign", ...onlyAllAttendance, async (req, res) => {
  const shiftId = Number(req.params.id);
  const b = z.object({ user_id: z.coerce.number().int().positive(), date: z.string().min(8) }).safeParse(req.body);
  if (!b.success) return res.status(422).json({ error: "Invalid payload" });
  const [existing] = await db.select({ id: employeeShiftSchedules.id }).from(employeeShiftSchedules).where(and(eq(employeeShiftSchedules.userId, b.data.user_id), sql`${employeeShiftSchedules.date} = ${b.data.date}`)).limit(1);
  if (existing) await db.update(employeeShiftSchedules).set({ employeeShiftId: shiftId } as never).where(eq(employeeShiftSchedules.id, existing.id));
  else await db.insert(employeeShiftSchedules).values({ companyId: req.auth!.companyId, userId: b.data.user_id, employeeShiftId: shiftId, date: b.data.date } as never);
  res.json({ ok: true, message: "Roster updated" });
});

/* ───────────────────────────── WFH requests ───────────────────────────── */
const weekend = (iso: string) => { const d = new Date(iso).getUTCDay(); return d === 0 || d === 6; };

attendanceRouter.get("/wfh", async (req, res) => {
  const userId = req.auth!.userId, companyId = req.auth!.companyId;
  const all = await seesAll(userId);
  const status = (req.query.status as string) || "";
  const conds: SQL[] = [eq(wfhRequests.companyId, companyId)];
  if (!all) conds.push(eq(wfhRequests.userId, userId));
  if (["pending", "approved", "rejected", "cancelled"].includes(status)) conds.push(eq(wfhRequests.status, status as never));
  const rows = await db
    .select({ id: wfhRequests.id, userId: wfhRequests.userId, user: users.name, wfhDate: wfhRequests.wfhDate, reason: wfhRequests.reason, status: wfhRequests.status, submittedAt: wfhRequests.submittedAt, decisionNote: wfhRequests.decisionNote })
    .from(wfhRequests).leftJoin(users, eq(users.id, wfhRequests.userId)).where(and(...conds)).orderBy(desc(wfhRequests.id));
  const today = new Date().toISOString().slice(0, 10);
  const allRows = await db.select({ status: wfhRequests.status, wfhDate: wfhRequests.wfhDate }).from(wfhRequests).where(and(eq(wfhRequests.companyId, companyId), ...(all ? [] : [eq(wfhRequests.userId, userId)])));
  const ad = (v: unknown) => (v instanceof Date ? v.toISOString().slice(0, 10) : String(v).slice(0, 10));
  const mo = new Date().getMonth();
  res.json({
    rows: rows.map((r) => ({ ...r, wfhDate: ad(r.wfhDate), canSeeAll: all })),
    kpis: {
      pending: allRows.filter((r) => r.status === "pending").length,
      approvedThisMonth: allRows.filter((r) => r.status === "approved" && new Date(ad(r.wfhDate)).getMonth() === mo).length,
      rejected: allRows.filter((r) => r.status === "rejected").length,
      onWfhToday: allRows.filter((r) => r.status === "approved" && ad(r.wfhDate) === today).length,
    },
    canApprove: all,
  });
});
attendanceRouter.post("/wfh", async (req, res) => {
  const b = z.object({ user_id: z.coerce.number().int().positive().optional(), from: z.string().min(8), to: z.string().min(8), reason: z.string().trim().min(5).max(500) }).safeParse(req.body);
  if (!b.success) return res.status(422).json({ error: "Invalid payload", details: b.error.flatten() });
  const all = await seesAll(req.auth!.userId);
  const targetUser = b.data.user_id && all ? b.data.user_id : req.auth!.userId; // only approvers file on behalf
  const dates: string[] = [];
  for (let d = new Date(b.data.from); d <= new Date(b.data.to); d.setUTCDate(d.getUTCDate() + 1)) {
    const iso = d.toISOString().slice(0, 10);
    if (!weekend(iso)) dates.push(iso); // skip Sat/Sun (legacy)
  }
  if (!dates.length) return res.status(422).json({ error: "No weekdays in that range" });
  for (const date of dates) {
    await db.insert(wfhRequests).values({ companyId: req.auth!.companyId, userId: targetUser, wfhDate: date, reason: b.data.reason, status: "pending", submittedBy: req.auth!.userId } as never);
  }
  res.status(201).json({ ok: true, message: `${dates.length} WFH day(s) submitted` });
});
attendanceRouter.post("/wfh/:id/decide", ...onlyAllAttendance, async (req, res) => {
  const id = Number(req.params.id);
  const b = z.object({ status: z.enum(["approved", "rejected"]), reason: z.string().optional() }).safeParse(req.body);
  if (!b.success) return res.status(422).json({ error: "Invalid payload" });
  const [exists] = await db.select({ id: wfhRequests.id }).from(wfhRequests).where(and(eq(wfhRequests.id, id), eq(wfhRequests.companyId, req.auth!.companyId))).limit(1);
  if (!exists) return res.status(404).json({ error: "Not found" });
  await db.update(wfhRequests).set({ status: b.data.status, decidedBy: req.auth!.userId, decidedAt: new Date(), decisionNote: b.data.reason ?? null }).where(eq(wfhRequests.id, id));
  res.json({ id, message: `WFH ${b.data.status}` });
});
attendanceRouter.post("/wfh/:id/cancel", async (req, res) => {
  const id = Number(req.params.id);
  const [row] = await db.select({ id: wfhRequests.id, userId: wfhRequests.userId, status: wfhRequests.status }).from(wfhRequests).where(and(eq(wfhRequests.id, id), eq(wfhRequests.companyId, req.auth!.companyId))).limit(1);
  if (!row) return res.status(404).json({ error: "Not found" });
  if (row.userId !== req.auth!.userId && !(await seesAll(req.auth!.userId))) return res.status(403).json({ error: "Forbidden" });
  if (row.status !== "pending") return res.status(422).json({ error: "Only pending requests can be cancelled" });
  await db.update(wfhRequests).set({ status: "cancelled" }).where(eq(wfhRequests.id, id));
  res.json({ id, message: "WFH cancelled" });
});

/* ───────────────────────────── Corrections ───────────────────────────── */
const REASON_CODES = ["forgot_in", "forgot_out", "forgot_both", "wrong_time", "wrong_tap", "other"] as const;

attendanceRouter.get("/corrections", async (req, res) => {
  const userId = req.auth!.userId, companyId = req.auth!.companyId;
  const all = await seesAll(userId);
  const status = (req.query.status as string) || "";
  const conds: SQL[] = [eq(attendanceCorrections.companyId, companyId)];
  if (!all) conds.push(eq(attendanceCorrections.userId, userId));
  if (["pending", "approved", "rejected", "cancelled"].includes(status)) conds.push(eq(attendanceCorrections.status, status as never));
  const rows = await db
    .select({ id: attendanceCorrections.id, userId: attendanceCorrections.userId, user: users.name, date: attendanceCorrections.correctionDate, reqIn: attendanceCorrections.requestedIn, reqOut: attendanceCorrections.requestedOut, reasonCode: attendanceCorrections.reasonCode, note: attendanceCorrections.note, status: attendanceCorrections.status, submittedAt: attendanceCorrections.submittedAt, decisionNote: attendanceCorrections.decisionNote })
    .from(attendanceCorrections).leftJoin(users, eq(users.id, attendanceCorrections.userId)).where(and(...conds)).orderBy(desc(attendanceCorrections.id));
  const ad = (v: unknown) => (v instanceof Date ? v.toISOString().slice(0, 10) : String(v).slice(0, 10));
  const allRows = await db.select({ status: attendanceCorrections.status, userId: attendanceCorrections.userId, submittedAt: attendanceCorrections.submittedAt }).from(attendanceCorrections).where(and(eq(attendanceCorrections.companyId, companyId), ...(all ? [] : [eq(attendanceCorrections.userId, userId)])));
  const mo = new Date().getMonth(), yr = new Date().getFullYear();
  const usedThisMonth = allRows.filter((r) => r.userId === userId && r.status !== "cancelled" && r.submittedAt && new Date(r.submittedAt).getMonth() === mo && new Date(r.submittedAt).getFullYear() === yr).length;
  res.json({
    rows: rows.map((r) => ({ ...r, date: ad(r.date), canSeeAll: all })),
    kpis: { pending: allRows.filter((r) => r.status === "pending").length, approved: allRows.filter((r) => r.status === "approved").length, rejected: allRows.filter((r) => r.status === "rejected").length },
    cap: { used: usedThisMonth, max: CORRECTION_CAP, exempt: all },
    reasonCodes: REASON_CODES,
    canApprove: all,
  });
});
attendanceRouter.get("/corrections/current-punches", async (req, res) => {
  const userId = (Number(req.query.user_id) && (await seesAll(req.auth!.userId))) ? Number(req.query.user_id) : req.auth!.userId;
  const date = (req.query.date as string) || new Date().toISOString().slice(0, 10);
  const rows = await db.select({ clockIn: attendances.clockInTime, clockOut: attendances.clockOutTime }).from(attendances).where(and(eq(attendances.userId, userId), sql`DATE(${attendances.clockInTime}) = ${date}`)).orderBy(asc(attendances.clockInTime));
  const t = (d: Date | null) => (d ? new Date(d).toISOString().slice(11, 16) : null);
  const [hol] = await db.select({ id: holidays.id, occasion: holidays.occassion }).from(holidays).where(and(eq(holidays.companyId, req.auth!.companyId), sql`${holidays.date} = ${date}`)).limit(1);
  const [lv] = await db.select({ id: leaves.id }).from(leaves).where(and(eq(leaves.userId, userId), eq(leaves.status, "approved"), sql`${leaves.leaveDate} = ${date}`)).limit(1);
  res.json({ in: rows[0] ? t(rows[0].clockIn as Date) : null, out: rows.length ? t(rows[rows.length - 1]!.clockOut as Date) : null, banner: hol ? `Holiday: ${hol.occasion ?? ""}` : lv ? "On approved leave this day" : null });
});
attendanceRouter.post("/corrections", async (req, res) => {
  const b = z.object({ user_id: z.coerce.number().int().positive().optional(), date: z.string().min(8), requested_in: z.string().optional().nullable(), requested_out: z.string().optional().nullable(), reason_code: z.enum(REASON_CODES), note: z.string().optional() }).safeParse(req.body);
  if (!b.success) return res.status(422).json({ error: "Invalid payload", details: b.error.flatten() });
  const all = await seesAll(req.auth!.userId);
  const targetUser = b.data.user_id && all ? b.data.user_id : req.auth!.userId;
  if (new Date(b.data.date) > new Date()) return res.status(422).json({ error: "Correction date can't be in the future" });
  // Monthly cap for non-admin self-filed (legacy policy; admins exempt).
  if (!all) {
    const mo = new Date().getMonth(), yr = new Date().getFullYear();
    const used = await db.select({ id: attendanceCorrections.id, submittedAt: attendanceCorrections.submittedAt, status: attendanceCorrections.status }).from(attendanceCorrections).where(and(eq(attendanceCorrections.userId, req.auth!.userId), ne(attendanceCorrections.status, "cancelled")));
    const n = used.filter((r) => r.submittedAt && new Date(r.submittedAt).getMonth() === mo && new Date(r.submittedAt).getFullYear() === yr).length;
    if (n >= CORRECTION_CAP) return res.status(422).json({ error: `You've used all ${CORRECTION_CAP} corrections this month` });
  }
  const [r] = await db.insert(attendanceCorrections).values({ companyId: req.auth!.companyId, userId: targetUser, correctionDate: b.data.date, requestedIn: b.data.requested_in ? hhmmss(b.data.requested_in) : null, requestedOut: b.data.requested_out ? hhmmss(b.data.requested_out) : null, reasonCode: b.data.reason_code, note: b.data.note ?? null, status: "pending", submittedBy: req.auth!.userId } as never).$returningId();
  res.status(201).json({ id: (r as { id: number }).id, message: "Correction submitted" });
});
attendanceRouter.post("/corrections/:id/decide", ...onlyAllAttendance, async (req, res) => {
  const id = Number(req.params.id);
  const b = z.object({ status: z.enum(["approved", "rejected"]), reason: z.string().optional() }).safeParse(req.body);
  if (!b.success) return res.status(422).json({ error: "Invalid payload" });
  const [c] = await db.select().from(attendanceCorrections).where(and(eq(attendanceCorrections.id, id), eq(attendanceCorrections.companyId, req.auth!.companyId))).limit(1);
  if (!c) return res.status(404).json({ error: "Not found" });
  let appliedId: number | null = null;
  if (b.data.status === "approved") {
    // Upsert an attendances row from the requested in/out (our model stores attendance in `attendances`).
    const date = c.correctionDate instanceof Date ? c.correctionDate.toISOString().slice(0, 10) : String(c.correctionDate).slice(0, 10);
    const clockIn = c.requestedIn ? new Date(`${date}T${c.requestedIn}`) : new Date(`${date}T09:00:00`);
    const clockOut = c.requestedOut ? new Date(`${date}T${c.requestedOut}`) : null;
    const [ins] = await db.insert(attendances).values({ companyId: req.auth!.companyId, userId: c.userId, clockInTime: clockIn, clockOutTime: clockOut, clockInIp: "correction", clockInType: "correction", late: "no", halfDay: "no" } as never).$returningId();
    appliedId = (ins as { id: number }).id;
  }
  await db.update(attendanceCorrections).set({ status: b.data.status, decidedBy: req.auth!.userId, decidedAt: new Date(), decisionNote: b.data.reason ?? null, appliedAttendanceId: appliedId }).where(eq(attendanceCorrections.id, id));
  res.json({ id, message: `Correction ${b.data.status}` });
});
attendanceRouter.post("/corrections/:id/cancel", async (req, res) => {
  const id = Number(req.params.id);
  const [row] = await db.select({ id: attendanceCorrections.id, userId: attendanceCorrections.userId, status: attendanceCorrections.status }).from(attendanceCorrections).where(and(eq(attendanceCorrections.id, id), eq(attendanceCorrections.companyId, req.auth!.companyId))).limit(1);
  if (!row) return res.status(404).json({ error: "Not found" });
  if (row.userId !== req.auth!.userId && !(await seesAll(req.auth!.userId))) return res.status(403).json({ error: "Forbidden" });
  if (row.status !== "pending") return res.status(422).json({ error: "Only pending requests can be cancelled" });
  await db.update(attendanceCorrections).set({ status: "cancelled" }).where(eq(attendanceCorrections.id, id));
  res.json({ id, message: "Correction cancelled" });
});
